<template>
	<div
		class="ao-info-banner d-flex flex-column"
		:class="[
			`ao-info-banner_${bannerType}`,
			{ 'ao-info-banner_with-content': $slots['default'] }
		]"
	>
		<div
			class="ao-info-banner__header"
			:class="[
				`ao-info-banner__header_${variant}`,
				{ 'ao-info-banner__header_with-action': !!actionType },
			]"
			@click="onBannerHeaderClick"
		>
			<AoIcon
				class="ao-info-banner__icon"
				:iconName="iconsMap[bannerType]"
			/>

			<span class="ao-info-banner__label">{{ label }}</span>

			<div class="ao-info-banner__value">
				<span class="ao-info-banner__value-text">{{ value }}</span>
				<template v-if="progressText">
					<div
						class="ao-info-banner__progress d-flex align-center"
						:style="{ color: `rgb(var(--v-theme-${progressIcon?.color}))` }"
					>
						<AoIcon :iconName="progressIcon?.name" />
						{{ progressText }}
					</div>
				</template>
			</div>

			<AoIcon
				v-if="actionType"
				class="ao-info-banner__actionIcon"
				:iconName="iconsMap[actionType]"
			/>
		</div>

		<div
			v-if="$slots['default']"
			class="ao-info-banner__content"
		>
			<slot name="default" />
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue'
import AoIcon from './AoIcon.vue'

const props = defineProps({
	label: {
		type: String,
	},
	value: {
		type: String,
	},
	bannerType: {
		type: String,
		default: 'default',
		validator: (v) => {
			return ['default', 'success', 'error', 'warning'].includes(v)
		},
	},
	variant: {
		type: String,
		default: 'advanced',
		validator: (v) => {
			return ['simple', 'advanced'].includes(v)
		},
	},
	progressText: {
		type: String,
	},
	actionType: {
		type: String, // edit, navigation
	},
})

const emit = defineEmits({
	'click:banner': null,
})

const iconsMap = {
	default: 'dangerCircle',
	success: 'checkInCircle',
	error: 'textDescription',
	warning: 'warningControl',
	edit: 'pen',
	navigation: 'groupOpen',
}

const progressIcon = computed(() => {
	if (props.bannerType == 'success') return { name: 'arrowUp', color: 'success' }
	else if (props.bannerType == 'error' || props.bannerType == 'warning')
		return { name: 'arrowDown', color: 'error' }

	return null
})

const onBannerHeaderClick = () => {
	if (props.actionType) emit('click:banner')
}
</script>

<style lang="scss">
@mixin banner-colors($color) {
	&_#{$color}.ao-info-banner_with-content {
		border: 1px solid rgba(var(--v-theme-#{$color}), 0.25);
	}

	&_#{$color} {
		.ao-info-banner__header {
			background-color: rgba(var(--v-theme-#{$color}), 0.1);
			color: rgb(var(--v-theme-status-#{$color}));
		}
	}
}

.ao-info-banner {
	border-radius: 8px;

	&__header {
		&_with-action {
			cursor: pointer;
		}

		&_advanced {
			display: grid;
			grid-template-columns: min-content auto min-content;
			column-gap: 12px;
		}

		&_simple {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 10px;
		}

		border-radius: 8px;
		padding: 12px;
	}

	&_with-content {
		.ao-info-banner__header {
			border-radius: 8px 8px 0 0;
		}
	}

	&__content {
		padding: 12px;
	}

	&__label {
		color: #86919d;
		grid-column: 2;
		font-size: 10px;
		font-weight: 500;
	}

	&__icon {
		grid-column: 1;
	}

	&__actionIcon {
		grid-column: 3;
	}

	&__icon,
	&__actionIcon {
		align-self: center;
		grid-row: 1 / span 2;
	}

	&__value {
		display: flex;
		align-items: center;
		grid-column: 2;
		font-size: 14px;
		font-weight: 600;
		gap: 4px;
	}

	&__progress {
		font-size: 12px;
	}

	@include banner-colors('success');
	@include banner-colors('warning');
	@include banner-colors('default');
	@include banner-colors('error');
}
</style>
